import { AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * One row per real customer, most-broken-first (the server sorts it that way).
 *
 * This is the microscope: for any single account you can see whether its data
 * is arriving, whether the engine spoke to it, and whether it acted — which is
 * the whole causal chain on one line.
 */

const WARNING_COPY: Record<string, string> = {
  no_connection: 'No POS connected',
  stale_sync: 'Sync has gone quiet',
  no_transactions: 'No transactions in 30d',
  brief_failures: 'Brief emails failing',
  no_recent_recs: 'No recommendation in 14d',
};

const ago = (stamp?: string | null) => {
  if (!stamp) return 'never';
  const then = new Date(stamp).getTime();
  if (!isFinite(then)) return 'never';
  const h = (Date.now() - then) / 3.6e6;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
  if (h < 48) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
};

export default function UserHealthTable({ rows }: { rows: any[] }) {
  if (!rows?.length) {
    return (
      <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-8 text-center shadow-sm mb-8">
        <p className="font-serif text-lg text-[#2D2A2E] mb-1">No customers yet</p>
        <p className="font-sans text-sm text-[#6B6670]">
          Every account you sign up will appear here with its own health row.
        </p>
      </div>
    );
  }

  const unhealthy = rows.filter((r) => r.warnings?.length).length;

  return (
    <div className="bg-white border border-[#E6E2DE] rounded-[14px] shadow-sm mb-8 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E2DE]">
        <h2 className="font-serif text-lg text-[#2D2A2E]">Per-customer health</h2>
        <span
          className={`font-sans text-xs px-2.5 py-0.5 rounded-full ${
            unhealthy
              ? 'bg-[#B54A4A]/10 text-[#B54A4A]'
              : 'bg-[#2E6B4F]/10 text-[#2E6B4F]'
          }`}
        >
          {unhealthy ? `${unhealthy} need attention` : 'all healthy'}
        </span>
      </div>

      {/* Wide table scrolls inside its own container so the page never does. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px] text-left">
          <thead>
            <tr className="border-b border-[#E6E2DE]">
              {['Customer', 'Plan', 'Last sync', 'Txns 30d', 'Briefs 7d', 'Recs', 'Found', 'Status'].map(
                (h) => (
                  <th
                    key={h}
                    className="font-sans text-[10px] text-[#6B6670] uppercase tracking-[0.12em] font-medium px-5 py-3 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const bad = r.warnings?.length > 0;
              return (
                <tr key={r.user_id} className="border-b border-[#F2F0ED] last:border-0 align-top">
                  <td className="px-5 py-3.5">
                    <p className="font-sans text-sm text-[#2D2A2E] leading-tight">
                      {r.business_name || r.name || '—'}
                    </p>
                    <p className="font-sans text-xs text-[#6B6670] leading-tight mt-0.5">
                      {r.email}
                    </p>
                  </td>
                  <td className="px-5 py-3.5 font-sans text-sm text-[#6B6670] capitalize whitespace-nowrap">
                    {r.plan || 'free'}
                    <span className="block text-xs text-[#6B6670]/70">
                      {r.days_since_signup}d old
                    </span>
                  </td>
                  <td
                    className={`px-5 py-3.5 font-sans text-sm whitespace-nowrap ${
                      r.warnings?.includes('stale_sync') || r.warnings?.includes('no_connection')
                        ? 'text-[#B54A4A]'
                        : 'text-[#6B6670]'
                    }`}
                  >
                    {ago(r.last_synced_at)}
                    <span className="block text-xs text-[#6B6670]/70">
                      {r.providers?.length ? r.providers.join(', ') : 'none'}
                    </span>
                  </td>
                  <td
                    className={`px-5 py-3.5 font-sans text-sm tabular-nums ${
                      r.transactions_30d ? 'text-[#2D2A2E]' : 'text-[#B54A4A]'
                    }`}
                  >
                    {(r.transactions_30d || 0).toLocaleString()}
                  </td>
                  <td className="px-5 py-3.5 font-sans text-sm text-[#2D2A2E] tabular-nums">
                    {r.briefs_sent_7d || 0}
                    {r.brief_failures_7d > 0 && (
                      <span className="text-[#B54A4A]"> · {r.brief_failures_7d} failed</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 font-sans text-sm text-[#2D2A2E] tabular-nums whitespace-nowrap">
                    {r.recs_adopted || 0}/{r.recs_sent || 0}
                    <span className="block text-xs text-[#6B6670]/70">adopted</span>
                  </td>
                  <td className="px-5 py-3.5 font-sans text-sm text-[#2E6B4F] tabular-nums">
                    ${Math.round(r.found_dollars || 0).toLocaleString()}
                  </td>
                  <td className="px-5 py-3.5">
                    {bad ? (
                      <div className="flex flex-col gap-1">
                        {r.warnings.map((w: string) => (
                          <span
                            key={w}
                            className="inline-flex items-center gap-1.5 font-sans text-xs text-[#B54A4A] whitespace-nowrap"
                          >
                            <AlertTriangle size={12} className="flex-shrink-0" />
                            {WARNING_COPY[w] || w}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 font-sans text-xs text-[#2E6B4F]">
                        <CheckCircle2 size={12} /> Healthy
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
